import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../UserContext';
import {
  ArrowLeft, ArrowRight, Mail, Lock, User, Shield,
  AlertCircle, Eye, EyeOff, Star, Sparkles, X
} from 'lucide-react';
import { sendEmailVerification } from 'firebase/auth';
import { loadStripe } from '@stripe/stripe-js';
import { trackStartTrial, trackCompleteRegistration, trackInitiateCheckout } from '../utils/metaConversions';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from '../components/CheckoutForm';
import { trackEvent } from '../utils/analyticsTracker';
import { usePageView } from '../utils/usePageView';

// Initialize Stripe once at module level — same pattern UpgradePage uses, so
// the two pages share the same Stripe instance and don't double-load the SDK.
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    'pk_live_51STW1JJz6mdvQ2vp3xyPBPyNDIQqGQj3VFDv0Wtc9npIC8C5sPRWiWwxE8rrymsEN4PU4iKfGb9SACPMhR0sSQVN00BoVcEgsX'
);

// Plan catalog — must match UpgradePage.js prices, since those are what Stripe
// actually charges (the .env price IDs map to these dollar amounts).
// `checkoutName` and `checkoutDisplayPrice` are what we pass to <CheckoutForm>,
// and they must match exactly what UpgradePage passes so CheckoutForm renders
// identically across both entry points.
const PLANS = {
  monthly: {
    id: 'monthly',
    name: 'Monthly Plan',
    priceLabel: '$10.99',
    period: '/month',
    billedAs: 'Billed monthly · cancel anytime',
    secondary: null,
    submitCopy: 'Create Account · $10.99/mo',
    checkoutName: 'Monthly',
    checkoutDisplayPrice: '$10.99',
  },
  annual: {
    id: 'annual',
    name: 'Annual Plan',
    priceLabel: '$7.99',
    period: '/month',
    billedAs: 'Billed $95.88/year · save 27% vs monthly',
    secondary: 'Most popular',
    submitCopy: 'Create Account · $7.99/mo billed annually',
    checkoutName: 'Annual',
    checkoutDisplayPrice: '$95.88',
  },
};

const AGE_OPTIONS = [
  { phase: 1, label: '1–2 years', subtitle: 'Toddler basics' },
  { phase: 2, label: '2–3 years', subtitle: 'Building words' },
  { phase: 3, label: '3–4 years', subtitle: 'Early sentences' },
  { phase: 4, label: '4–5 years', subtitle: 'Reading readiness' },
];

// Build a username that won't collide. UserContext.signup() requires unique
// usernames; we never expose this to the parent. Email prefix + 4 random chars
// is collision-resistant enough for our scale and stays human-readable in admin
// dashboards if anyone ever looks at it.
const buildUsername = (email) => {
  const prefix = (email.split('@')[0] || 'parent').toLowerCase().replace(/[^a-z0-9_-]/g, '');
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${prefix.slice(0, 16)}_${suffix}`;
};

const GetStartedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signup, updateProfile, createPaymentIntent } = useUser();

  // Plan must arrive via router state from a pricing CTA. Direct visitors get
  // bounced to /upgrade so they can pick a plan first — we don't want anyone
  // landing on this page without knowing what they're buying.
  const initialPlanId = location.state?.plan === 'monthly' || location.state?.plan === 'annual'
    ? location.state.plan
    : null;
  const [planId, setPlanId] = useState(initialPlanId || 'annual');
  const plan = useMemo(() => PLANS[planId], [planId]);

  // Form state
  const [childAge, setChildAge] = useState(null); // 1-4 (phase number)
  const [childName, setChildName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [topError, setTopError] = useState('');

  // Post-signup checkout state — mirrors the pattern used by UpgradePage so
  // CheckoutForm + verification modal behave identically on both pages.
  //   pendingCheckoutPlan: set right after signup succeeds. A dedicated effect
  //     waits for the Firebase `user` state to propagate, writes the profile
  //     fields, then kicks off initiateCheckout(). Cleared once checkout is
  //     initiated.
  //   isPostSignupFlow: "sticky" flag that stays true for the rest of the
  //     page's life once the user has submitted. Used to stop the
  //     "already-logged-in → bounce to /upgrade" effect from firing the
  //     instant signup completes (which would otherwise teleport the user
  //     away mid-flow).
  //   clientSecret / selectedPlanDetails / showCheckoutForm: identical to
  //     UpgradePage, drive the <CheckoutForm> modal.
  //   showVerificationModal / verificationSent / verificationError /
  //   pendingPlanType: identical to UpgradePage's email-verification gate.
  //   checkoutError: top-of-page error when createPaymentIntent itself fails.
  const [pendingCheckoutPlan, setPendingCheckoutPlan] = useState(null);
  const [isPostSignupFlow, setIsPostSignupFlow] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [pendingPlanType, setPendingPlanType] = useState(null);

  // Bounce direct visits with no plan, and bail if no plan was supplied
  useEffect(() => {
    if (!initialPlanId) {
      navigate('/upgrade', { replace: true });
    }
  }, [initialPlanId, navigate]);

  // Page view fires once per session (deduped). The plan is captured as
  // extraMeta so the dashboard can attribute the page view to the chosen plan.
  usePageView('get-started', { extraMeta: { plan: initialPlanId || 'unknown' } });
  // get_started_view is a separate trackEvent (not deduped) so changes to
  // initialPlanId after mount still register — useful for users who land on
  // /get-started, switch plans via the URL, and then convert.
  useEffect(() => {
    trackEvent('get_started_view', { plan: initialPlanId || 'unknown' }, null);
  }, [initialPlanId]);

  // Already-logged-in users (who arrived at /get-started directly) skip to
  // /upgrade with the plan in state. We explicitly DO NOT bounce during the
  // post-signup flow, because the freshly-created user pops into this same
  // `user` value — without the guard, the user would teleport to /upgrade
  // the instant their account was created, losing the in-flight checkout.
  useEffect(() => {
    if (user && !isPostSignupFlow) {
      navigate('/upgrade', { replace: true, state: { plan: planId, fromGetStarted: true } });
    }
  }, [user, planId, navigate, isPostSignupFlow]);

  const validate = () => {
    const next = {};
    if (!childAge) next.childAge = "Pick your child's age.";
    if (!childName.trim()) next.childName = "Enter a name or nickname.";
    if (childName.trim().length > 40) next.childName = "Keep it under 40 characters.";
    if (!email.trim()) next.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "That email doesn't look right.";
    if (!password) next.password = "Password is required.";
    else if (password.length < 6) next.password = "At least 6 characters.";
    if (password && confirmPassword !== password) next.confirmPassword = "Passwords don't match.";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTopError('');
    setCheckoutError('');
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      trackEvent('get_started_error', { stage: 'validation', plan: planId }, null);
      return;
    }

    setSubmitting(true);
    // Flip the sticky post-signup flag BEFORE calling signup(). This stops the
    // "logged-in → bounce to /upgrade" effect from firing the moment the
    // Firebase auth state propagates.
    setIsPostSignupFlow(true);
    trackEvent('cta_click', { button: 'get_started_submit', plan: planId, ageGroup: AGE_OPTIONS.find(a => a.phase === childAge)?.label }, null);

    // Step 1: create the auth account. Auto-derived username keeps the form
    // clean — the parent never has to know about the username concept.
    let signupResult = await signup(
      email.trim(),
      password,
      buildUsername(email.trim()),
      emailOptIn,
      null
    );

    // Username collision is rare but recoverable — retry once with a fresh suffix.
    if (!signupResult.success && /username/i.test(signupResult.error || '')) {
      signupResult = await signup(
        email.trim(),
        password,
        buildUsername(email.trim()),
        emailOptIn,
        null
      );
    }

    if (!signupResult.success) {
      setSubmitting(false);
      setIsPostSignupFlow(false);
      trackEvent('get_started_error', { stage: 'signup', plan: planId, message: signupResult.error }, null);
      // Map common errors to the field where they belong
      if (/exists/i.test(signupResult.error || '')) {
        setErrors(prev => ({ ...prev, email: 'An account with this email already exists. Try logging in instead.' }));
      } else {
        setTopError(signupResult.error || 'Something went wrong creating your account.');
      }
      return;
    }

    trackEvent('signup', { email: email.trim(), source: 'get_started', plan: planId, phase: childAge }, email.trim());
    try { if (window.BrightReadingPixel?.startTrial) window.BrightReadingPixel.startTrial(); } catch {}
    // Meta Conversions API — CompleteRegistration + StartTrial
    const capiUserData = { em: email.trim(), fn: childName.trim() };
    trackCompleteRegistration(capiUserData);
    trackStartTrial(capiUserData);

    // Queue the checkout. A dedicated effect (below) waits for the Firebase
    // `user` state to propagate, writes the profile fields via updateProfile
    // (which requires `user` to be set), then calls initiateCheckout(). We do
    // NOT reset `submitting` here — the button keeps its spinner until the
    // CheckoutForm modal opens on top of the form.
    setPendingCheckoutPlan(planId);
  };

  // ─── Post-signup → checkout pipeline ───────────────────────────────────
  // Runs once Firebase has propagated the new `user` state after signup.
  // Writes the personalization fields (currentPhase + childName + planType)
  // that needed a logged-in user, then hands off to initiateCheckout().
  useEffect(() => {
    if (!user || !pendingCheckoutPlan) return;
    let cancelled = false;
    (async () => {
      try {
        await updateProfile({
          currentPhase: childAge,
          childName: childName.trim(),
          planType: pendingCheckoutPlan,
        });
      } catch (err) {
        console.warn('Profile update failed (non-fatal):', err);
        trackEvent('get_started_error', { stage: 'profile', plan: pendingCheckoutPlan, message: err?.message }, null);
      }
      if (cancelled) return;
      const planToCheckout = pendingCheckoutPlan;
      setPendingCheckoutPlan(null);
      initiateCheckout(planToCheckout);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pendingCheckoutPlan]);

  // Email-verification polling — mirrors UpgradePage exactly. If the user
  // clicks the verification link in another tab, user.reload() picks up the
  // new `emailVerified` flag and we auto-resume checkout for the plan that
  // was pending when the modal opened.
  useEffect(() => {
    if (!user || user.emailVerified || !pendingPlanType) return;
    let cancelled = false;
    const interval = setInterval(() => {
      user.reload().then(() => {
        if (!cancelled && user.emailVerified) {
          clearInterval(interval);
          initiateCheckout(pendingPlanType);
        }
      }).catch(() => {});
    }, 3000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!pendingPlanType]);

  // Lock background scroll while the checkout modal is open — same as
  // UpgradePage. The page's existing scroll position is restored on close.
  useEffect(() => {
    if (showCheckoutForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [showCheckoutForm]);

  // ─── Checkout handlers (mirror UpgradePage) ────────────────────────────
  // initiateCheckout = UpgradePage's handleUpgrade, renamed so it doesn't
  // collide with anything and so the intent is obvious.
  const initiateCheckout = async (planType) => {
    if (!user) return;

    // Email verification gate. UpgradePage has the same gate — we don't skip
    // it just because the account was created seconds ago. Security behavior
    // stays consistent across both entry points.
    if (!user.emailVerified) {
      setPendingPlanType(planType);
      setShowVerificationModal(true);
      return;
    }

    setCheckoutError('');

    try {
      const result = await createPaymentIntent(planType);

      if (!result.success) {
        setSubmitting(false);
        setCheckoutError(result.error || 'Failed to start checkout. Please try again.');
        trackEvent('get_started_error', { stage: 'payment_intent', plan: planType, message: result.error }, null);
        return;
      }

      const planRecord = PLANS[planType];
      setClientSecret(result.clientSecret);
      setSelectedPlanDetails({
        name: planRecord.checkoutName,
        price: planRecord.checkoutDisplayPrice,
        displayPrice: planRecord.checkoutDisplayPrice,
      });
      setShowCheckoutForm(true);
      setSubmitting(false);
      // Meta Conversions API — InitiateCheckout
      trackInitiateCheckout({ em: user.email, external_id: user.uid });
    } catch (error) {
      console.error('Checkout error:', error);
      setSubmitting(false);
      setCheckoutError('Something went wrong. Please try again.');
      trackEvent('get_started_error', { stage: 'payment_intent', plan: planType, message: error?.message }, null);
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    console.log('Payment successful from /get-started!', paymentIntent);
    trackEvent('subscription_start', { source: 'get_started', plan: pendingPlanType || planId }, null);
    // Brief delay so the Stripe webhook has a moment to update Firestore
    // before /payment-success reads the user doc.
    setTimeout(() => {
      navigate('/payment-success');
    }, 1000);
  };

  const handleCancelCheckout = () => {
    setShowCheckoutForm(false);
    setClientSecret('');
    setSelectedPlanDetails(null);
    trackEvent('cta_click', { button: 'get_started_checkout_cancel', plan: pendingPlanType || planId }, null);
  };

  const handleSendVerification = async () => {
    if (!user) return;
    try {
      setVerificationError('');
      const actionCodeSettings = {
        // After clicking the verification link the visitor lands back on
        // /get-started. If they already have a logged-in session the normal
        // bounce-to-upgrade effect will take them to the right place.
        url: `${window.location.origin}/get-started`,
        handleCodeInApp: false,
      };
      await sendEmailVerification(user, actionCodeSettings);
      setVerificationSent(true);
    } catch (error) {
      console.error('Verification email error:', error);
      let errorMsg = 'Failed to send verification email. Please try again.';
      if (error.code === 'auth/too-many-requests') {
        errorMsg = 'Too many requests. Please wait a moment before trying again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMsg = 'Invalid email address.';
      } else if (error.code === 'auth/user-not-found') {
        errorMsg = 'User account not found.';
      }
      setVerificationError(errorMsg);
    }
  };

  const handleVerificationClose = () => {
    setShowVerificationModal(false);
    setVerificationSent(false);
    setVerificationError('');
    setPendingPlanType(null);
    setSubmitting(false);
  };

  const switchPlan = () => {
    const next = planId === 'annual' ? 'monthly' : 'annual';
    setPlanId(next);
    trackEvent('cta_click', { button: 'get_started_switch_plan', from: planId, to: next }, null);
  };

  const goToLogin = () => {
    trackEvent('cta_click', { button: 'get_started_login_existing' }, null);
    navigate('/login', { state: { prefilledEmail: email.trim() || undefined, mode: 'login' } });
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-amber-50 via-orange-50 to-pink-50 animate-page-enter">
      {/* Nav */}
      <nav className="bg-white shadow-md p-2.5 sm:p-4 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1 text-purple-600 hover:text-purple-800 transition mr-1 sm:mr-2"
              title="Back"
            >
              <ArrowLeft size={20} />
            </button>
            <img src="/assets/brlogo2.webp" alt="BrightReading" className="h-6 sm:h-8 w-auto object-contain" />
            <h1 className="text-base sm:text-xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">BrightReading</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10 pb-16">
        {/* Plan reflection card — anchors the decision the parent already made */}
        <div className={`relative rounded-2xl shadow-xl p-5 sm:p-6 mb-6 sm:mb-8 ${
          planId === 'annual'
            ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white'
            : 'bg-white border-2 border-purple-200 text-gray-900'
        }`}>
          {plan.secondary && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-400 text-white text-[11px] sm:text-xs font-black px-3 py-1 rounded-full shadow-md whitespace-nowrap">
              {plan.secondary}
            </div>
          )}
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className={planId === 'annual' ? 'text-yellow-200' : 'text-purple-500'} />
              <p className={`text-xs sm:text-sm font-bold uppercase tracking-wide ${planId === 'annual' ? 'text-white/80' : 'text-purple-600'}`}>You're starting with</p>
            </div>
          </div>
          <h2 className={`text-2xl sm:text-3xl font-black mb-1 ${planId === 'annual' ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h2>
          <div className="flex items-baseline gap-1 mb-1">
            <span className={`text-3xl sm:text-4xl font-black ${planId === 'annual' ? 'text-white' : 'text-gray-900'}`}>{plan.priceLabel}</span>
            <span className={`text-base sm:text-lg ${planId === 'annual' ? 'text-white/70' : 'text-gray-400'}`}>{plan.period}</span>
          </div>
          <p className={`text-sm ${planId === 'annual' ? 'text-white/80' : 'text-gray-500'}`}>{plan.billedAs}</p>
          <button
            type="button"
            onClick={switchPlan}
            className={`mt-3 inline-flex items-center gap-1 text-xs sm:text-sm font-bold underline underline-offset-2 transition ${
              planId === 'annual' ? 'text-white/90 hover:text-white' : 'text-purple-600 hover:text-purple-800'
            }`}
          >
            Switch to {planId === 'annual' ? 'Monthly' : 'Annual'} plan
          </button>
        </div>

        {topError && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-start gap-2">
            <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{topError}</p>
          </div>
        )}

        {checkoutError && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-start gap-3">
            <X className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-semibold">{checkoutError}</p>
              <button
                type="button"
                onClick={() => setCheckoutError('')}
                className="text-red-600 text-xs underline mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {/* About your child */}
          <section className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 sm:p-7">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg sm:text-xl font-black text-gray-900">About your child</h3>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-5">We use this to tune the activities to your child's age and use their name in the lesson scripts.</p>

            <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Child's age</label>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2">
              {AGE_OPTIONS.map((opt) => {
                const selected = childAge === opt.phase;
                return (
                  <button
                    key={opt.phase}
                    type="button"
                    onClick={() => { setChildAge(opt.phase); setErrors(prev => ({ ...prev, childAge: undefined })); }}
                    className={`text-left rounded-xl border-2 p-3 sm:p-4 transition ${
                      selected
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-gray-200 bg-white hover:border-purple-300'
                    }`}
                  >
                    <div className={`text-sm sm:text-base font-black ${selected ? 'text-purple-700' : 'text-gray-900'}`}>{opt.label}</div>
                    <div className="text-[11px] sm:text-xs text-gray-500">{opt.subtitle}</div>
                  </button>
                );
              })}
            </div>
            {errors.childAge && <p className="text-xs text-red-600 mb-3 mt-1">{errors.childAge}</p>}

            <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2 mt-4 sm:mt-5">Child's name or nickname</label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={childName}
                onChange={(e) => { setChildName(e.target.value); setErrors(prev => ({ ...prev, childName: undefined })); }}
                placeholder="e.g. Mia, Jojo, Bug"
                maxLength={41}
                className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 outline-none transition text-sm sm:text-base ${
                  errors.childName
                    ? 'border-red-300 focus:border-red-400'
                    : 'border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100'
                }`}
              />
            </div>
            {errors.childName && <p className="text-xs text-red-600 mt-1">{errors.childName}</p>}
            <div className="mt-2 flex items-start gap-2 text-[11px] sm:text-xs text-gray-500">
              <Shield size={13} className="text-green-600 flex-shrink-0 mt-0.5" />
              <p>We never share or sell your information. Your child's name only appears inside the activities you do together.</p>
            </div>
          </section>

          {/* Your account */}
          <section className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 sm:p-7">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="text-lg sm:text-xl font-black text-gray-900">Your account</h3>
              <button type="button" onClick={goToLogin} className="text-xs sm:text-sm font-bold text-purple-600 hover:text-purple-800 transition">
                Already have an account? Log in
              </button>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-5">You'll use this to sign in on any device.</p>

            <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Email</label>
            <div className="relative mb-1">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: undefined })); }}
                placeholder="you@example.com"
                autoComplete="email"
                className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 outline-none transition text-sm sm:text-base ${
                  errors.email
                    ? 'border-red-300 focus:border-red-400'
                    : 'border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100'
                }`}
              />
            </div>
            {errors.email && <p className="text-xs text-red-600 mt-1 mb-2">{errors.email}</p>}

            <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2 mt-4">Password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErrors(prev => ({ ...prev, password: undefined })); }}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                className={`w-full pl-10 pr-10 py-3 rounded-xl border-2 outline-none transition text-sm sm:text-base ${
                  errors.password
                    ? 'border-red-300 focus:border-red-400'
                    : 'border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}

            <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2 mt-4">Confirm password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setErrors(prev => ({ ...prev, confirmPassword: undefined })); }}
                placeholder="Type it again"
                autoComplete="new-password"
                className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 outline-none transition text-sm sm:text-base ${
                  errors.confirmPassword
                    ? 'border-red-300 focus:border-red-400'
                    : 'border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100'
                }`}
              />
            </div>
            {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>}

            <label className="flex items-start gap-2 mt-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={emailOptIn}
                onChange={(e) => setEmailOptIn(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400"
              />
              <span className="text-xs sm:text-sm text-gray-600">Send me free reading tips by email (optional).</span>
            </label>
          </section>

          {/* Submit */}
          <div>
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-4 rounded-xl font-black text-base sm:text-lg shadow-lg transition flex items-center justify-center gap-2 ${
                submitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
              }`}
            >
              {submitting ? 'Creating your account…' : (
                <>
                  {plan.submitCopy}
                  <ArrowRight size={20} />
                </>
              )}
            </button>
            <p className="text-center text-[11px] sm:text-xs text-gray-400 mt-3">
              You'll add your payment details on the next step.
            </p>
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 pt-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-gray-500 font-semibold">
              <Shield size={13} className="text-green-600" />Cancel anytime
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-gray-500 font-semibold">
              <Lock size={13} className="text-green-600" />Encrypted &amp; secure
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-gray-500 font-semibold">
              <Star size={13} className="text-yellow-500 fill-yellow-500" />Real-parent reviews
            </span>
          </div>
        </form>
      </div>

      {/* ─── Embedded Stripe checkout modal ─────────────────────────────
          Mounted identically to UpgradePage.js — same <Elements> wrapper,
          same <CheckoutForm> component, same clientSecret. The only
          difference is *when* it mounts: on UpgradePage after a plan
          click; here, after signup + profile write + createPaymentIntent
          all complete in a single submit pipeline. */}
      {showCheckoutForm && clientSecret && selectedPlanDetails && (
        <div className="fixed inset-0 bg-black/70 z-50 overflow-y-auto">
          <div className="min-h-screen min-h-[100dvh] flex items-center justify-center p-4 py-8">
            <div className="my-8 w-full">
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm
                  planName={selectedPlanDetails.name}
                  planPrice={selectedPlanDetails.displayPrice || selectedPlanDetails.price}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handleCancelCheckout}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}

      {/* ─── Email verification modal ──────────────────────────────────
          Mirrors UpgradePage's gate exactly. Shown when the account was
          just created (so emailVerified === false) and the parent tries
          to proceed to checkout. The useEffect above polls user.reload()
          every 3s while this is showing; once verified, initiateCheckout
          runs automatically for the pendingPlanType. */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-4 sm:p-8 text-center max-h-[95dvh] sm:max-h-[90vh] overflow-y-auto">
            <div className="mb-6">
              <div className="bg-gradient-to-br from-blue-400 to-purple-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Mail className="text-white" size={40} />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">
                {verificationSent ? 'Check Your Email!' : 'Verify Your Email'}
              </h2>
              <p className="text-gray-600 font-semibold">
                {verificationSent
                  ? `We've sent a verification link to ${user?.email}`
                  : 'One quick step before payment: please verify your email address.'}
              </p>
            </div>

            {verificationError && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                <div className="bg-red-100 rounded-full p-1 flex-shrink-0">
                  <X className="text-red-600" size={20} />
                </div>
                <p className="text-red-700 font-semibold text-sm text-left">{verificationError}</p>
              </div>
            )}

            {verificationSent ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border-2 border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">Next Steps:</h3>
                  <ol className="space-y-2 text-left text-gray-700 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-blue-600">1.</span>
                      <span>Check your inbox for our verification email</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-blue-600">2.</span>
                      <span>Click the verification link in the email</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-blue-600">3.</span>
                      <span>Come back here to complete your checkout</span>
                    </li>
                  </ol>
                </div>
                <div className="text-xs text-gray-500">
                  <p className="mb-2">Didn't receive the email? Check your spam folder.</p>
                  <button
                    type="button"
                    onClick={handleSendVerification}
                    className="text-purple-600 hover:text-purple-700 font-bold underline"
                  >
                    Resend verification email
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleVerificationClose}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 rounded-xl font-black text-lg hover:shadow-xl transition"
                >
                  Got It!
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-400 rounded-xl p-4 text-left">
                  <p className="text-blue-900 text-sm font-semibold">
                    📧 We'll send a verification link to: <br />
                    <span className="font-black">{user?.email}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSendVerification}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 rounded-xl font-black text-lg hover:shadow-xl transition flex items-center justify-center gap-2"
                >
                  <Mail size={20} />
                  Send Verification Email
                </button>
                <button
                  type="button"
                  onClick={handleVerificationClose}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-semibold transition"
                >
                  Maybe Later
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GetStartedPage;
